<script>
	// @ts-nocheck
	import slugify from 'slugify';
	import { ArrowRight, Clock, Map } from 'lucide-svelte';
	import { Badge } from '$lib/components/ui/badge';
	import { Button } from '$lib/components/ui/button';
	import * as Card from '$lib/components/ui/card';
	import * as Tooltip from '$lib/components/ui/tooltip';
	import { enhancedImages } from '$lib/utils/images';
	import { trip } from '$lib/utils/tripStore';
	import { toast } from 'svelte-sonner';

	// `logoOverlay` and `externalHref` are opt-in per call site. The first centres an
	// operator logo over the card image; the second points the card at an off-site URL
	// opened in a new tab instead of this site's tour page. Only used for the Coal
	// River Wine Hopper, which is run by our sister company; every other tour renders
	// and links exactly as before.
	let { tour, logoOverlay = false, externalHref = null } = $props();

	function isEnhancedImage(imageUrl) {
		if (!imageUrl || imageUrl.includes('http://') || imageUrl.includes('https://')) return false;
		try {
			return !!Object.entries(enhancedImages).find(([key]) => key === imageUrl);
		} catch {
			return false;
		}
	}

	function getImageSource(imageUrl) {
		if (!imageUrl) return null;
		try {
			const entry = Object.entries(enhancedImages).find(([key]) => key === imageUrl);
			if (entry?.[1]?.default) return entry[1].default;
		} catch {}
		return imageUrl;
	}

	function formatDuration(minutes) {
		const hours = Math.ceil(minutes / 60);
		if (hours >= 24) {
			const days = Math.ceil(hours / 24);
			return `${days} day${days > 1 ? 's' : ''}`;
		}
		return `${hours} hour${hours > 1 ? 's' : ''}`;
	}

	function formatPrice(price) {
		return `From AUD $${price.toFixed(2)}`;
	}

	function formatProductType(productType) {
		const labels = {
			DAYTOUR: 'Day Tour',
			MULTIDAYTOUR: 'Multi-day',
			SHOREEXCURSION: 'Shore Excursion',
			PRIVATETOUR: 'Private'
		};
		return labels[productType] ?? productType;
	}

	function addToTrip(e) {
		e.preventDefault();
		e.stopPropagation();
		trip.update((currentTrips) => {
			if (currentTrips.some((t) => t.productCode === tour.productCode)) {
				toast.error(`${tour.name} is already in your trip`);
				return currentTrips;
			}
			toast.success(`${tour.name} added to trip`);
			return [...currentTrips, tour];
		});
	}

	const href = $derived(
		externalHref ??
			`/tours/${slugify(tour.name, { lower: true, strict: true, remove: /[*+~.()\'"!:@]/g })}`
	);
	const imageUrl = $derived(tour.images?.[0]?.itemUrl ?? null);
</script>

<a
	{href}
	target={externalHref ? '_blank' : null}
	rel={externalHref ? 'noopener noreferrer' : null}
	data-sveltekit-prefetch={externalHref ? null : ''}
	data-sveltekit-noscroll={externalHref ? null : ''}
	class="block h-full w-full text-left"
>
	<Card.Root
		class="group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border-none bg-muted shadow-none"
	>
		<Card.Header class="relative w-full p-0">
			<div class="relative h-56 w-full overflow-hidden rounded-t-2xl">
				{#if imageUrl}
					{#if isEnhancedImage(imageUrl)}
						<enhanced:img
							alt={tour.name}
							class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							src={getImageSource(imageUrl)}
							sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
							loading="lazy"
						/>
					{:else}
						<img
							alt={tour.name}
							class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							src={imageUrl}
							loading="lazy"
							onerror={(e) => {
								e.currentTarget.onerror = null;
								e.currentTarget.src = '/images/placeholder-tour.jpg';
							}}
						/>
					{/if}
				{:else}
					<div class="flex h-full w-full items-center justify-center bg-muted">
						<span class="text-sm text-muted-foreground">No image available</span>
					</div>
				{/if}
				<div
					class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"
				></div>
				{#if logoOverlay}
					<div
						class="pointer-events-none absolute inset-0 flex items-center justify-center bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0)_70%)]"
					>
						<enhanced:img
							src="$lib/assets/logo/winehopper-black.png?w=320"
							alt="The Wine Hopper"
							class="h-auto w-40 brightness-0 drop-shadow-md invert"
							sizes="160px"
							loading="lazy"
						/>
					</div>
				{/if}
				<Badge class="absolute left-4 top-4 bg-[#ffd451] text-foreground hover:bg-[#ffd451]/80">
					{formatProductType(tour.productType)}
				</Badge>
				<div class="absolute bottom-0 left-0 right-0 px-4 pb-4">
					<h3 class="line-clamp-2 font-heading text-lg font-semibold leading-snug text-white">
						{tour.name}
					</h3>
				</div>
			</div>
			<div class="flex items-center gap-4 px-4 pb-3 pt-4">
				<span class="text-base font-semibold">{formatPrice(tour.advertisedPrice)}</span>
				<span class="flex items-center gap-1 text-sm text-muted-foreground">
					<Clock class="h-3.5 w-3.5" />
					{formatDuration(tour.durationMinutes)}
				</span>
			</div>
		</Card.Header>
		<Card.Content class="flex-1 px-4 pb-5 pt-0">
			<p class="line-clamp-3 text-sm text-muted-foreground">{tour.shortDescription}</p>
		</Card.Content>
		<Card.Footer class="flex justify-start px-4 pb-4">
			<Tooltip.Provider delayDuration={150}>
				<Tooltip.Root>
					<Tooltip.Trigger>
						{#snippet child({ props })}
							<Button
								{...props}
								onclick={addToTrip}
								variant="ghost"
								size="icon"
								class="h-8 w-8 rounded-full bg-muted-foreground/10 hover:bg-muted-foreground/20"
								aria-label="Add to trip planner"
							>
								<Map class="h-4 w-4" />
							</Button>
						{/snippet}
					</Tooltip.Trigger>
					<Tooltip.Content>Add to Trip</Tooltip.Content>
				</Tooltip.Root>
			</Tooltip.Provider>
		</Card.Footer>
		<div
			class="absolute bottom-4 right-4 translate-x-8 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-60"
		>
			<ArrowRight class="h-6 w-6" />
		</div>
	</Card.Root>
	{#if externalHref}
		<span class="sr-only">(opens in a new tab)</span>
	{/if}
</a>
